// "File a bug": capture what the browser already knows and send it.
//
// Nothing here instruments the app. Every value is read at the moment the
// button is pressed, from APIs the browser keeps anyway: the URL, the DOM
// (headings, selected tabs, open dialogs, visible alerts), the Performance
// timeline (recent network calls with timings and, in Chromium, status), and
// navigator/screen. The server adds the reporter's own page-view/API trail
// from the AuditLogs table, which was already being written.
//
// Tokens never leave: URL parameters that look like credentials are
// redacted, and only `nexus.*` localStorage keys are read (the OIDC session
// lives under other keys).

import { getUser } from '../auth/authService';

const ANALYTICS = `${process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL ?? ''}`;
const SECRET_PARAM = /(token|code|state|sig|secret|password|pwd|key|session|auth|se$|sp$|sv$|skoid|sktid)/i;

/** A URL with credential-looking query parameters replaced. */
export function redactUrl(raw) {
    try {
        const u = new URL(raw, window.location.origin);
        u.searchParams.forEach((_, k) => {
            if (SECRET_PARAM.test(k)) u.searchParams.set(k, '[redacted]');
        });
        return u.toString();
    } catch {
        return String(raw || '').replace(/([?&][^=&]*(token|sig|code|secret)[^=&]*=)[^&]*/gi, '$1[redacted]');
    }
}

const text = (el, max = 140) => (el?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, max);
const uniq = (xs) => [...new Set(xs.filter(Boolean))];
const all = (sel) => Array.from(document.querySelectorAll(sel));
const visible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

function describeElement(el) {
    if (!el || el === document.body) return '';
    const bits = [el.tagName.toLowerCase()];
    if (el.id) bits.push(`#${el.id}`);
    const label = el.getAttribute('aria-label') || el.getAttribute('name') || el.getAttribute('placeholder') || text(el, 60);
    return `${bits.join('')}${label ? ` "${label}"` : ''}`;
}

function pageSection() {
    const url = redactUrl(window.location.href);
    const params = {};
    new URL(url).searchParams.forEach((v, k) => { params[k] = v; });
    let explain = '';
    try { explain = window.localStorage.getItem('nexus.explain') === '1' ? 'on' : 'off'; } catch { /* ignore */ }
    return {
        URL: url,
        Path: window.location.pathname,
        'Query parameters': Object.keys(params).length ? params : '',
        'Document title': document.title,
        Headings: uniq(all('h1, h2, h3, h4, h5, h6').filter(visible).map((h) => text(h, 80))).slice(0, 12),
        // MUI tabs use aria-selected; Client Overview's own tabs a class.
        'Selected tabs': uniq(all('[role="tab"][aria-selected="true"], .co-tab-active, [aria-current="page"]')
            .filter(visible).map((t) => text(t, 60))),
        'Open dialogs': uniq(all('[role="dialog"], [role="presentation"] .MuiDialog-paper')
            .filter(visible).map((d) => text(d.querySelector('h2, h1, .MuiDialogTitle-root') || d, 80))),
        'Alerts and errors on screen': uniq(all('[role="alert"], .MuiAlert-message, .Mui-error, .MuiFormHelperText-root.Mui-error')
            .filter(visible).map((a) => text(a, 200))).slice(0, 10),
        'Focused element': describeElement(document.activeElement),
        Scroll: `${Math.round(window.scrollX)}, ${Math.round(window.scrollY)} of ${document.documentElement.scrollWidth} x ${document.documentElement.scrollHeight}`,
        'Explain mode': explain,
        Referrer: document.referrer ? redactUrl(document.referrer) : '',
    };
}

function sessionSection() {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    return {
        'Time since this page loaded': `${Math.round(performance.now() / 1000)} s`,
        'How the page was reached': nav?.type || '',
        'Page load (ms)': nav ? Math.round(nav.loadEventEnd || nav.domComplete || 0) : '',
        'History entries in this tab': window.history.length,
        'Local time': new Date().toString(),
    };
}

/** The last calls the page made to any service, newest first. */
export function recentNetwork(limit = 30) {
    const entries = (performance.getEntriesByType?.('resource') || [])
        .filter((e) => e.initiatorType === 'fetch' || e.initiatorType === 'xmlhttprequest');
    const now = performance.now();
    return entries.slice(-limit).reverse().map((e) => {
        let host = '';
        let path = e.name;
        try {
            const u = new URL(redactUrl(e.name));
            host = u.host;
            path = `${u.pathname}${u.search}`.slice(0, 160);
        } catch { /* keep raw */ }
        const status = e.responseStatus || '';
        return {
            ago: `${Math.round((now - e.startTime) / 1000)} s`,
            host,
            path,
            status: status || (e.transferSize === 0 && e.decodedBodySize === 0 ? 'no response / cached' : ''),
            ms: Math.round(e.duration),
            ...(status >= 400 ? { flag: 'FAILED' } : {}),
        };
    });
}

function browserSection() {
    const n = window.navigator;
    const conn = n.connection || {};
    const mem = performance.memory;
    return {
        'User agent': n.userAgent,
        Platform: n.userAgentData?.platform || n.platform || '',
        Languages: (n.languages || [n.language]).join(', '),
        'Time zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        Viewport: `${window.innerWidth} x ${window.innerHeight}`,
        Screen: `${window.screen?.width} x ${window.screen?.height} @ ${window.devicePixelRatio}x`,
        'Prefers dark': window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'yes' : 'no',
        Online: n.onLine ? 'yes' : 'no',
        Connection: conn.effectiveType ? `${conn.effectiveType}, ~${conn.downlink} Mb/s, rtt ${conn.rtt} ms` : '',
        'CPU cores': n.hardwareConcurrency || '',
        'Device memory (GB)': n.deviceMemory || '',
        'JS heap used (MB)': mem ? Math.round(mem.usedJSHeapSize / 1048576) : '',
    };
}

function appSection() {
    const host = (u) => { try { return new URL(u).host; } catch { return u || ''; } };
    const prefs = {};
    try {
        for (let i = 0; i < window.localStorage.length; i += 1) {
            const k = window.localStorage.key(i);
            if (k && k.startsWith('nexus.')) prefs[k] = String(window.localStorage.getItem(k)).slice(0, 120);
        }
    } catch { /* storage blocked */ }
    return {
        Environment: process.env.NODE_ENV,
        'Build / version': process.env.REACT_APP_VERSION || process.env.REACT_APP_BUILD_ID || '',
        'Analytics service': host(process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL),
        'Integrations service': host(process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL),
        'Theme on page': document.documentElement.getAttribute('data-theme') || document.body.className || '',
        'Nexus preferences': Object.keys(prefs).length ? prefs : '',
    };
}

async function userSection() {
    try {
        const p = (await getUser())?.profile || {};
        const raw = p['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? p.role ?? [];
        return { Name: p.name || '', Email: p.email || p.preferred_username || '', Roles: Array.isArray(raw) ? raw : [raw] };
    } catch {
        return { Name: '', Email: '', Roles: [] };
    }
}

/** Everything, grouped into the sections the email and ticket render. */
export async function captureContext() {
    const safe = (fn) => { try { return fn(); } catch (e) { return { error: String(e?.message || e) }; } };
    return {
        Page: safe(pageSection),
        Session: safe(sessionSection),
        'Recent network calls (newest first)': safe(() => recentNetwork()),
        User: await userSection(),
        Browser: safe(browserSection),
        App: safe(appSection),
    };
}

/**
 * The visible viewport as a JPEG data URL, or null. Elements marked
 * data-bug-report-exclude (the button itself) are left out. Never throws:
 * a report without a screenshot is still a report.
 */
export async function captureScreenshot({ timeoutMs = 10000 } = {}) {
    try {
        const { toJpeg } = await import('html-to-image');
        const opts = {
            quality: 0.8,
            pixelRatio: 1,
            cacheBust: true,
            backgroundColor: window.getComputedStyle(document.body).backgroundColor || '#0b1220',
            width: window.innerWidth,
            height: window.innerHeight,
            style: { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)` },
            filter: (node) => !(node?.dataset && 'bugReportExclude' in node.dataset),
        };
        const shot = (extra) => Promise.race([
            toJpeg(document.body, { ...opts, ...extra }),
            new Promise((_, rej) => setTimeout(() => rej(new Error('timed out')), timeoutMs)),
        ]);
        try {
            return await shot({});
        } catch {
            // Cross-origin font CSS is the usual failure; retry without fonts.
            return await shot({ skipFonts: true });
        }
    } catch {
        return null;
    }
}

export async function fileBugReport({ description, expected, context, screenshot, auto = false }) {
    const user = await getUser();
    const res = await fetch(`${ANALYTICS}/api/bug-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.access_token}` },
        body: JSON.stringify({ description, expected, context, screenshot: screenshot || null, ...(auto ? { auto: true } : {}) }),
    });
    let data = null;
    try { data = await res.json(); } catch { /* non-JSON error */ }
    if (!res.ok) {
        const d = data?.detail;
        throw new Error(typeof d === 'string' ? d : `HTTP ${res.status}`);
    }
    return data;
}
